import type { Localized } from "./types"
import { company } from "./company"

interface PrivacySection {
  title: Localized<string>
  paragraphs?: Localized<string[]>[]
  list?: Localized<string[]>
  highlight?: { label: Localized<string>; value: Localized<string> }
  complaint?: { title: Localized<string>; body: Localized<string> }
}

export const privacyContent = {
  title: { nl: "Privacyverklaring", en: "Privacy Statement" },
  intro: {
    nl: `${company.name}, gevestigd in ${company.displayLocation.city}, ${company.displayLocation.country}, is verantwoordelijk voor de verwerking van persoonsgegevens zoals weergegeven in deze privacyverklaring.`,
    en: `${company.name}, located in ${company.displayLocation.city}, ${company.displayLocation.countryEn}, is responsible for the processing of personal data as shown in this privacy statement.`,
  },
  contactBlock: {
    title: { nl: "Contactgegevens:", en: "Contact details:" },
    websiteUrl: "https://schmeitzdegroenailabs.com/",
    address: {
      nl: `${company.displayLocation.city}, ${company.displayLocation.country}`,
      en: `${company.displayLocation.city}, ${company.displayLocation.countryEn}`,
    },
    phone: "31637557073",
    officer: {
      nl: `Camiel Schmeitz is de Functionaris Gegevensbescherming van ${company.name} Hij/zij is te bereiken via ${company.email}`,
      en: `Camiel Schmeitz is the Data Protection Officer of ${company.name}. He/she can be reached via ${company.email}`,
    },
  },
  sections: [
    {
      title: { nl: "Persoonsgegevens die wij verwerken", en: "Personal data we process" },
      paragraphs: [
        {
          nl: [`${company.name} verwerkt uw persoonsgegevens doordat u gebruik maakt van onze diensten en/of omdat u deze zelf aan ons verstrekt.`],
          en: [`${company.name} processes your personal data because you use our services and/or because you provide them to us yourself.`],
        },
        {
          nl: ["Hieronder vindt u een overzicht van de persoonsgegevens die wij verwerken:"],
          en: ["Below you will find an overview of the personal data we process:"],
        },
      ],
      list: {
        nl: ["Voor- en achternaam", "Telefoonnummer", "E-mailadres"],
        en: ["First and last name", "Phone number", "Email address"],
      },
    },
    {
      title: {
        nl: "Bijzondere en/of gevoelige persoonsgegevens die wij verwerken",
        en: "Special and/or sensitive personal data we process",
      },
      paragraphs: [
        {
          nl: [
            "Onze website en/of dienst heeft niet de intentie gegevens te verzamelen over websitebezoekers die jonger zijn dan 16 jaar. Tenzij ze toestemming hebben van ouders of voogd. We kunnen echter niet controleren of een bezoeker ouder dan 16 is. Wij raden ouders dan ook aan betrokken te zijn bij de online activiteiten van hun kinderen, om zo te voorkomen dat er gegevens over kinderen verzameld worden zonder ouderlijke toestemming. Als u er van overtuigd bent dat wij zonder die toestemming persoonlijke gegevens hebben verzameld over een minderjarige, neem dan contact met ons op via info@schmeitzdegroenailabs.com, dan verwijderen wij deze informatie.",
          ],
          en: [
            "Our website and/or service does not intend to collect data about website visitors younger than 16 years of age. Unless they have permission from parents or guardian. However, we cannot check whether a visitor is older than 16. We therefore recommend that parents be involved in the online activities of their children, in order to prevent data about children from being collected without parental consent. If you are convinced that we have collected personal information about a minor without that consent, please contact us via info@schmeitzdegroenailabs.com, and we will delete this information.",
          ],
        },
      ],
    },
    {
      title: {
        nl: `${company.name} verwerkt jouw persoonsgegevens voor de volgende doelen:`,
        en: `${company.name} processes your personal data for the following purposes:`,
      },
      list: {
        nl: ["U te kunnen bellen of e-mailen indien dit nodig is om onze dienstverlening uit te kunnen voeren"],
        en: ["To be able to call or email you if this is necessary to perform our services"],
      },
    },
    {
      title: { nl: "Hoe lang we persoonsgegevens bewaren", en: "How long we store personal data" },
      paragraphs: [
        {
          nl: [
            `${company.name} bewaart uw persoonsgegevens niet langer dan strikt nodig is om de doelen te realiseren waarvoor uw gegevens worden verzameld. Wij hanteren de volgende bewaartermijnen voor de volgende (categorieën) van persoonsgegevens:`,
          ],
          en: [
            `${company.name} does not store your personal data for longer than is strictly necessary to realize the goals for which your data is collected. We handle the following storage periods for the following (categories) of personal data:`,
          ],
        },
      ],
      highlight: {
        label: { nl: "Bewaartermijn", en: "Retention period" },
        value: { nl: "12 maanden", en: "12 months" },
      },
    },
    {
      title: { nl: "Delen van persoonsgegevens met derden", en: "Sharing personal data with third parties" },
      paragraphs: [
        {
          nl: [
            `${company.name} verstrekt uitsluitend aan derden en alleen als dit nodig is voor de uitvoering van onze overeenkomst met u of om te voldoen aan een wettelijke verplichting.`,
          ],
          en: [
            `${company.name} only provides to third parties and only if this is necessary for the execution of our agreement with you or to comply with a legal obligation.`,
          ],
        },
      ],
    },
    {
      title: { nl: "Cookies, of vergelijkbare technieken, die wij gebruiken", en: "Cookies, or similar techniques, that we use" },
      paragraphs: [
        {
          nl: [
            `${company.name} gebruikt alleen technische en functionele cookies. En analytische cookies die geen inbreuk maken op uw privacy. Een cookie is een klein tekstbestand dat bij het eerste bezoek aan deze website wordt opgeslagen op uw computer, tablet of smartphone. De cookies die wij gebruiken zijn noodzakelijk voor de technische werking van de website en uw gebruiksgemak. Ze zorgen ervoor dat de website naar behoren werkt en onthouden bijvoorbeeld uw voorkeursinstellingen. Ook kunnen wij hiermee onze website optimaliseren. U kunt zich afmelden voor cookies door uw internetbrowser zo in te stellen dat deze geen cookies meer opslaat. Daarnaast kunt u ook alle informatie die eerder is opgeslagen via de instellingen van uw browser verwijderen.`,
          ],
          en: [
            `${company.name} only uses technical and functional cookies. And analytical cookies that do not infringe on your privacy. A cookie is a small text file that is stored on your computer, tablet or smartphone when you first visit this website. The cookies we use are necessary for the technical operation of the website and your ease of use. They ensure that the website works properly and, for example, remember your preference settings. We can also use this to optimize our website. You can opt out of cookies by setting your internet browser so that it no longer stores cookies. In addition, you can also delete all information previously stored via the settings of your browser.`,
          ],
        },
      ],
    },
    {
      title: { nl: "Gegevens inzien, aanpassen of verwijderen", en: "Viewing, adjusting or deleting data" },
      paragraphs: [
        {
          nl: [
            "U heeft het recht om uw persoonsgegevens in te zien, te corrigeren of te verwijderen. Daarnaast heeft u het recht om uw eventuele toestemming voor de gegevensverwerking in te trekken of bezwaar te maken tegen de verwerking van uw persoonsgegevens door Schmeitz De Groen AI Labs en heeft u het recht op gegevensoverdraagbaarheid. Dat betekent dat u bij ons een verzoek kunt indienen om de persoonsgegevens die wij van u beschikken in een computerbestand naar u of een ander, door u genoemde organisatie, te sturen.",
            "U kunt een verzoek tot inzage, correctie, verwijdering, gegevensoverdraging van uw persoonsgegevens of verzoek tot intrekking van uw toestemming of bezwaar op de verwerking van uw persoonsgegevens sturen naar info@schmeitzdegroenailabs.com.",
            "Om er zeker van te zijn dat het verzoek tot inzage door u is gedaan, vragen wij u een kopie van uw identiteitsbewijs met het verzoek mee te sturen. Maak in deze kopie uw pasfoto, MRZ (machine readable zone, de strook met nummers onderaan het paspoort), paspoortnummer en Burgerservicenummer (BSN) zwart. Dit ter bescherming van uw privacy. We reageren zo snel mogelijk, maar binnen vier weken, op uw verzoek.",
          ],
          en: [
            "You have the right to view, correct or delete your personal data. You also have the right to withdraw your consent to data processing or to object to the processing of your personal data by Schmeitz De Groen AI Labs and you have the right to data portability. This means that you can submit a request to us to send the personal data we have about you in a computer file to you or another organization mentioned by you.",
            "You can send a request for inspection, correction, deletion, data transfer of your personal data or request for withdrawal of your consent or objection to the processing of your personal data to info@schmeitzdegroenailabs.com.",
            "To ensure that the request for inspection has been made by you, we ask you to send a copy of your identity document with the request. In this copy, make your passport photo, MRZ (machine readable zone, the strip with numbers at the bottom of the passport), passport number and Citizen Service Number (BSN) black. This is to protect your privacy. We will respond as quickly as possible, but within four weeks, to your request.",
          ],
        },
      ],
      complaint: {
        title: { nl: "Klacht indienen", en: "Submit a complaint" },
        body: {
          nl: `${company.name} wil u er tevens op wijzen dat u de mogelijkheid heeft om een klacht in te dienen bij de nationale toezichthouder, de Autoriteit Persoonsgegevens. Dat kan via de volgende link: https://autoriteitpersoonsgegevens.nl/nl/contact-met-de-autoriteit-persoonsgegevens/tip-ons`,
          en: `${company.name} would also like to point out that you have the option of filing a complaint with the national supervisor, the Dutch Data Protection Authority (Autoriteit Persoonsgegevens). This can be done via the following link: https://autoriteitpersoonsgegevens.nl/nl/contact-met-de-autoriteit-persoonsgegevens/tip-ons`,
        },
      },
    },
    {
      title: { nl: "Hoe wij persoonsgegevens beveiligen", en: "How we secure personal data" },
      paragraphs: [
        {
          nl: [
            `${company.name} neemt de bescherming van uw gegevens serieus en neemt passende maatregelen om misbruik, verlies, onbevoegde toegang, ongewenste openbaarmaking en ongeoorloofde wijziging tegen te gaan. Als u de indruk heeft dat uw gegevens niet goed beveiligd zijn of er aanwijzingen zijn van misbruik, neem dan contact met ons op via de klantenservice of via info@schmeitzdegroenailabs.com`,
          ],
          en: [
            `${company.name} takes the protection of your data seriously and takes appropriate measures to prevent misuse, loss, unauthorized access, unwanted disclosure and unauthorized modification. If you have the impression that your data is not properly secured or there are indications of misuse, please contact our customer service or via info@schmeitzdegroenailabs.com`,
          ],
        },
      ],
    },
  ] satisfies PrivacySection[],
}
